import { useEffect, useState } from 'react';
import type { PushSubscriptionInput } from '@private-messenger/shared';

import { readPwaPlatformState } from './pwa-platform.js';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installPrompt: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installPrompt = event as InstallPromptEvent;
    installListeners.forEach((listener) => listener());
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    installListeners.forEach((listener) => listener());
  });
}

export function useInstallPrompt(): {
  available: boolean;
  manual: boolean;
  installed: boolean;
  install: () => Promise<boolean>;
} {
  const [, refresh] = useState(0);
  useEffect(() => {
    const listener = () => refresh((value) => value + 1);
    const displayMode = window.matchMedia('(display-mode: standalone)');
    installListeners.add(listener);
    displayMode.addEventListener('change', listener);
    return () => {
      installListeners.delete(listener);
      displayMode.removeEventListener('change', listener);
    };
  }, []);
  const platform = readPwaPlatformState();
  const manual = platform.iosLike && !platform.installed && !installPrompt;
  return {
    available: !platform.installed && (Boolean(installPrompt) || manual),
    manual,
    installed: platform.installed,
    install: async () => {
      if (!installPrompt) return false;
      const prompt = installPrompt;
      await prompt.prompt();
      const accepted = (await prompt.userChoice).outcome === 'accepted';
      installPrompt = null;
      installListeners.forEach((listener) => listener());
      return accepted;
    },
  };
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function createPushSubscription(publicKey: string): Promise<{
  browserSubscription: PushSubscription;
  input: PushSubscriptionInput;
}> {
  if (!pushSupported()) throw new Error('Этот браузер не поддерживает Web Push');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Разрешение на уведомления не предоставлено');
  const registration = await navigator.serviceWorker.ready;
  const browserSubscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    }));
  const serialized = browserSubscription.toJSON();
  if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys.auth) {
    throw new Error('Браузер вернул неполную push-подписку');
  }
  return {
    browserSubscription,
    input: {
      endpoint: serialized.endpoint,
      keys: { p256dh: serialized.keys.p256dh, auth: serialized.keys.auth },
    },
  };
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob((value + padding).replaceAll('-', '+').replaceAll('_', '/'));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}
